import {
  CalculationImport,
  CompanyImport,
  EInvoicingRequest,
  EInvoiceAddress,
  InvoiceSettingsCheck,
  InvoicingRequest,
  PaymentChannel,
  WorkerCheck,
  WorkerImport,
} from "../model";
import { Ajax } from "./infra";

/**
 * Methods for mass data uploads.
 */
export class Imports {

  /**
   * For NG1-dependency injection
   * @ignore
   */
  public static $inject = ["AjaxNg1"];

  /** Base URL for imports. */
  private baseUrl = "/v03/api/import";

  constructor(private ajax: Ajax) {}


  /**
   * Imports companies.
   *
   * @param companies The company import data.
   * @returns A Promise with result data ( PartnerCompanyAccountInfo ).
   */
  public importCompanies(companies: CompanyImport[]): Promise<CompanyImport[]> {
    return this.ajax.postJSON(`${this.baseUrl}/companies`, companies);
  }

  /**
   * Imports workers.
   *
   * @param workers The worker import data.
   * @returns A Promise with result data containing worker IDs.
   */
  public importWorkers(workers: WorkerImport[]): Promise<WorkerImport[]> {
    return this.ajax.postJSON(`${this.baseUrl}/workers`, workers);
  }

  /**
   * Imports calculations to a new/existing payroll.
   *
   * @param calculations The calculation import data.
   * @returns A Promise with result data containing payroll IDs.
   */
    public importCalculations(calculations: CalculationImport[]): Promise<CalculationImport[]> {
      return this.ajax.postJSON(`${this.baseUrl}/calculations`, calculations);
    }

  /**
   * Creates invoices for payrolls.
   *
   * @param payrolls IDs of the payrolls to create invoices for.
   * @param channel The payment channel.
   * @returns A Promise with result data containing created invoice objects.
   */
    public createInvoices(payrolls: InvoicingRequest[], channel: PaymentChannel): Promise<InvoicingRequest[]> {
      return this.ajax.postJSON(`${this.baseUrl}/invoicing/${channel}`, payrolls);
    }

   /**
    * Sends invoices as e-invoices.
    *
    * @param invoices IDs of the invoices to be sent as e-invoices.
    * @returns A Promise with result data containing e-invoicing result messages.
    */
    public sendEInvoices(invoices: EInvoicingRequest[]): Promise<EInvoicingRequest[]> {
      return this.ajax.postJSON(`${this.baseUrl}/einvoicing`, invoices);
    }

  /**
   * Checks the existence of the given workers.
   * The given ID can be worker's Salaxy ID, employment ID, social security number of external ID.
   *
   * @param workers The worker IDs to check.
   * @returns A Promise with result data containing worker objects.
   */
    public checkWorkers(workers: WorkerCheck[]): Promise<WorkerCheck[]> {
      return this.ajax.postJSON(`${this.baseUrl}/workers-check`, workers);
    }

  /**
   * Checks the existence of the invoice settings for a company,
   * The given ID can be company's Salaxy ID or official ID.
   *
   * @param companies The company IDs to check.
   * @param type The type of the check: sepa or eInvoice settings.
   * @returns A Promise with result data containing settings objects.
   */
    public checkInvoiceSettings(companies: InvoiceSettingsCheck[], type: "sepa" | "eInvoice"): Promise<InvoiceSettingsCheck[]> {
      return this.ajax.postJSON(`${this.baseUrl}/invoice-settings-check/${type}`, companies);
    }

    /**
     * Query e-invoice addresses for a company.
     * @param officialId Official ID of the company.
     * @returns List of e-invoice addresses.
     */
    public queryEInvoiceAddress(officialId: string): Promise<EInvoiceAddress[]> {
      return this.ajax.getJSON(`${this.baseUrl}/einvoice/receiver?officialId=${encodeURIComponent(officialId)}`);
    }
}